
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Trash2, Tag, Percent, DollarSign } from "lucide-react";
import { useAvailableMembershipCoupons } from "@/hooks/useMembershipCoupons";

interface MembershipCouponSelectorProps {
  providerId: string;
  membershipPrice: number;
  onCouponSelect: (userCouponId: string, discountAmount: number) => void;
  onCouponRemove: () => void;
  selectedCouponId?: string;
  appliedDiscount?: number;
  membershipType?: string;
}

const MembershipCouponSelector = ({
  providerId,
  membershipPrice,
  onCouponSelect,
  onCouponRemove,
  selectedCouponId,
  appliedDiscount = 0,
  membershipType = 'pack'
}: MembershipCouponSelectorProps) => {
  const [couponCode, setCouponCode] = useState("");
  const { data: availableCoupons, isLoading } = useAvailableMembershipCoupons(providerId);

  // Find selected coupon
  const selectedCoupon = availableCoupons?.find(uc => uc.id === selectedCouponId);

  // Determine if this is a membership or package based on type
  const isMembership = membershipType.toLowerCase() === 'unlimited' || membershipType.toLowerCase() === 'sessions_per_month';
  const priceLabel = isMembership ? 'Membership Price:' : 'Package Price:';

  const calculateDiscount = (coupon: any) => {
    if (coupon.discount_type === 'percentage') {
      return Math.min((membershipPrice * coupon.discount_value) / 100, membershipPrice);
    } else if (coupon.discount_type === 'fixed') {
      return Math.min(coupon.discount_value, membershipPrice);
    }
    return 0;
  };

  const handleCouponSelect = (userCoupon: any) => {
    const discountAmount = calculateDiscount(userCoupon.coupon);
    onCouponSelect(userCoupon.id, discountAmount);
  };

  const finalPrice = membershipPrice - appliedDiscount;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Apply Coupon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            <div className="h-10 bg-gray-300 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Tag className="h-5 w-5" />
          Apply Coupon
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selected coupon display */}
        {selectedCoupon && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-green-900">{selectedCoupon.coupon.name}</h4>
                <p className="text-sm text-green-700">
                  Code: <span className="font-mono">{selectedCoupon.coupon.code}</span>
                </p>
                <p className="text-sm text-green-700">
                  Discount: £{appliedDiscount.toFixed(2)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onCouponRemove}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Price summary */}
        <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
          <div className="flex justify-between">
            <span>{priceLabel}</span>
            <span>£{membershipPrice.toFixed(2)}</span>
          </div>
          {appliedDiscount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount:</span>
              <span>-£{appliedDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold border-t pt-2">
            <span>Total:</span>
            <span>£{finalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Available coupons */}
        {!selectedCoupon && availableCoupons && availableCoupons.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-3 block">
              Available Coupons
            </Label>
            <div className="space-y-2">
              {availableCoupons.map((userCoupon) => {
                const discount = calculateDiscount(userCoupon.coupon);
                return (
                  <div
                    key={userCoupon.id}
                    className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleCouponSelect(userCoupon)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium">{userCoupon.coupon.name}</h4>
                        <p className="text-sm text-gray-600">
                          Code: <span className="font-mono">{userCoupon.coupon.code}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {userCoupon.coupon.discount_type === 'percentage' ? (
                              <>
                                <Percent className="h-3 w-3 mr-1" />
                                {userCoupon.coupon.discount_value}% off
                              </>
                            ) : (
                              <>
                                <DollarSign className="h-3 w-3 mr-1" />
                                £{userCoupon.coupon.discount_value} off
                              </>
                            )}
                          </Badge>
                          <span className="text-sm text-green-600 font-medium">
                            Save £{discount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        Apply
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No coupons available */}
        {(!availableCoupons || availableCoupons.length === 0) && !selectedCoupon && (
          <div className="text-center py-6 text-gray-500">
            <Tag className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>No coupons available for this membership.</p>
            <p className="text-sm mt-1">Check your profile for available coupons.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MembershipCouponSelector;
